/* eslint-disable indent */
import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useSnackbar } from 'notistack'
import { Grid, TextField } from '@mui/material'

import { updateFlowStart, cacheUpdateFlowGroupStartAction } from '../../../../../../actions'
import { textFieldTypes, variantTypes } from '../../../../../../constants'
import { Logger, LoggerTypes, checkUpdateResponse, checkErrorResponse } from '../../../../../../utils'
import { FlowElementBottomButtons } from '../../../FlowElementBottomButtons'
import '../../../common-elements-style.scss'

const defaultValue = {
  name: '',
  description: ''
}

export const FlowElementStartAboutPanel = ({
  currentProject,
  currentScenario,
  currentFlowGroup,
  currentStart,
  // -- refresh the view after saving
  refreshAfterSave
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()

  const nameValue = `${currentStart?.name ? currentStart?.name : ''}` || defaultValue.name
  const descriptionValue = `${currentStart?.description ? currentStart?.description : ''}` || defaultValue.description

  const [name, setName] = useState(nameValue)
  const [description, setDescription] = useState(descriptionValue)

  const [errors, setErrors] = useState(null)

  useEffect(() => {
    setName(nameValue)
    setDescription(descriptionValue)
  }, [currentStart])

  const onClickSave = (e) => {
    e.stopPropagation()

    const pid = currentProject?.id || null
    const sid = currentScenario?.id || null
    const fid = currentFlowGroup?.id || null

    const newStart = {
      id: currentStart.id,
      name: name === '' ? null : name,
      description: description === '' ? null : description
    }

    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementStartAboutPanel.onClickSave()', { newStart })

    dispatch(updateFlowStart(pid, sid, fid, newStart))
      .then(async (res) => {
        Logger.trace(
          LoggerTypes.CONTROLLER,
          'FlowElementStartAboutPanel.onClickSave().dispatch(updateFlowStart(...)).then()',
          res
        )
        checkUpdateResponse(res, enqueueSnackbar, t)

        // Now the start has been updated in the back-end we propagate the change in the REDUX state cache
        Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementStartAboutPanel.onSaveTask().cacheUpdateFlowGroupTask()')
        dispatch(cacheUpdateFlowGroupStartAction(currentFlowGroup, res.data))

        // -- remove the previous errors
        await setErrors(null)

        // -- reload the element data.
        if (refreshAfterSave && typeof refreshAfterSave === 'function') {
          refreshAfterSave(currentFlowGroup, [res.data])
        }
      })
      .catch((res) => {
        Logger.trace(
          LoggerTypes.WARN,
          'FlowElementStartAboutPanel.onClickSave().dispatch(updateFlowStart(...)).catch()',
          {
            res
          }
        )

        checkErrorResponse(res, enqueueSnackbar, t)

        if (res?.response?.data?.fields) {
          setErrors(res.response.data.fields)
        }
      })
  }

  // --

  const onChangeName = (e) => {
    e.stopPropagation()
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementStartAboutPanel.onChangeTitle()', { name: _value })
    setName(_value)
  }

  const onChangeDescription = (e) => {
    e.stopPropagation()
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementStartAboutPanel.onChangeDescription()', {
      description: _value
    })
    setDescription(_value)
  }

  Logger.trace(LoggerTypes.CONTAINER, 'FlowElementStartAboutPanel.render()', {
    currentStart,
    about: {
      name,
      description
    },
    errors
  })

  return (
    <div className="FlowElementStartAboutPanel">
      <form>
        {/* == ABOUT == */}
        <Grid container spacing={2} style={{ padding: '30px 10px 20px' }}>
          {/*  name  */}
          <Grid item xs={12} sm={12}>
            <TextField
              type={textFieldTypes.TEXT}
              name="name"
              id="name"
              value={name}
              variant={variantTypes.OUTLINED}
              label={t('flowActions.start.panels.about.name.label')}
              className="common_fullwidth"
              onChange={onChangeName}
              placeholder={t('flowActions.start.panels.about.name.placeholder')}
              error={Boolean(errors && errors?.name)}
              helperText={(errors && errors?.name) || ''}
            />
          </Grid>

          {/*  description  */}
          <Grid item xs={12} sm={12} className="common_fullwidth" style={{ marginTop: '15px' }}>
            <TextField
              type={textFieldTypes.TEXT}
              name="description"
              id="description"
              value={description}
              variant={variantTypes.OUTLINED}
              label={t('flowActions.start.panels.about.description.label')}
              className="common_fullwidth"
              onChange={onChangeDescription}
              placeholder={t('flowActions.start.panels.about.description.placeholder')}
              error={Boolean(errors && errors?.description)}
              helperText={(errors && errors?.description) || ''}
            />
          </Grid>
        </Grid>
        <FlowElementBottomButtons onSave={onClickSave} />
      </form>
    </div>
  )
}

FlowElementStartAboutPanel.displayName = 'FlowElementStartAboutPanel'

FlowElementStartAboutPanel.propTypes = {
  currentProject: PropTypes.object.isRequired,
  currentScenario: PropTypes.object.isRequired,
  currentFlowGroup: PropTypes.object.isRequired,
  currentStart: PropTypes.object,
  // -- Flow's Action
  start: PropTypes.arrayOf(PropTypes.object),
  // -- refresh after saving
  refreshAfterSave: PropTypes.func
}

FlowElementStartAboutPanel.defaultProps = {
  currentProject: null,
  currentScenario: null,
  currentFlowGroup: null,
  currentStart: null,
  // -- Flow's Action
  start: [],
  // -- refresh after saving
  refreshAfterSave: null
}
